import { useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";

const SPEEDS = [1, 1.5, 2];
const BARS = 72;
/** A bigger file is streamed without a drawn waveform instead of being downloaded whole */
const MAX_DECODE_BYTES = 12 * 1024 * 1024;
const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

/** A steady, plausible wave for when the real one can't be drawn (huge file, or the browser can't decode it) */
function placeholderPeaks(seed: string): number[] {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return Array.from({ length: BARS }, (_, i) => {
    h = Math.imul(h ^ (i + 1), 2246822519) >>> 0;
    return 0.22 + 0.5 * ((Math.sin(i / 5.5) + 1) / 2) * (0.55 + (h % 1000) / 2200);
  });
}

/** Loudest sample in each of BARS slices of the audio, scaled so the loudest bar fills the height */
async function readPeaks(data: ArrayBuffer): Promise<number[] | null> {
  const Ctx: typeof AudioContext | undefined = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  try {
    const buf = await ctx.decodeAudioData(data);
    const ch = buf.getChannelData(0);
    const size = Math.max(1, Math.floor(ch.length / BARS));
    const peaks = Array.from({ length: BARS }, (_, i) => {
      let max = 0;
      const start = i * size;
      // Looking at every ~50th sample is plenty to find the peak of a slice
      for (let j = start; j < Math.min(ch.length, start + size); j += 50) max = Math.max(max, Math.abs(ch[j] || 0));
      return max;
    });
    const top = Math.max(...peaks, 0.001);
    return peaks.map((p) => Math.max(0.08, p / top));
  } catch {
    return null;
  } finally {
    void ctx.close().catch(() => undefined);
  }
}

/**
 * Audio player with a waveform: play/pause, bars drawn from the real audio that fill as it plays (click or use the arrow
 * keys to jump), the time, and playback speed. Used for call recordings in the Calls drawer (by call id) and ringtone
 * previews (by address). The file is fetched once, so a recording counts as a single play in the security log.
 */
export function CallPlayer({ callId, src, onError, autoPlay = false, volume, showSpeed = true, onEnded }: { callId?: string; src?: string; onError: () => void; autoPlay?: boolean; /** 0 to 1 */ volume?: number; showSpeed?: boolean; onEnded?: () => void }) {
  const url = src ?? `/api/Call/Recording/${callId}`;
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [media, setMedia] = useState<string | null>(null);
  const fallback = useMemo(() => placeholderPeaks(url), [url]);

  // A transcript line ("crm:call-seek" with this call's id) plays from that moment
  useEffect(() => {
    if (!callId) return;
    const onSeek = (e: Event) => {
      const d = (e as CustomEvent<{ callId: string; sec: number }>).detail;
      const a = ref.current;
      if (!a || d?.callId !== callId) return;
      a.currentTime = Math.max(0, d.sec);
      setTime(a.currentTime);
      void a.play().catch(() => undefined);
    };
    window.addEventListener("crm:call-seek", onSeek);
    return () => window.removeEventListener("crm:call-seek", onSeek);
  }, [callId]);

  // Fetch the audio once: the same bytes draw the waveform and play
  useEffect(() => {
    setPlaying(false);
    setTime(0);
    setLength(0);
    setSpeed(1);
    setPeaks(null);
    setMedia(null);
    const abort = new AbortController();
    let objectUrl: string | null = null;
    (async () => {
      try {
        const res = await fetch(url, { signal: abort.signal, credentials: "same-origin" });
        if (!res.ok) return onError();
        const size = Number(res.headers.get("content-length") || 0);
        if (!size || size > MAX_DECODE_BYTES) {
          // Too big to hold: stream it with the audio element instead (this request is dropped)
          void res.body?.cancel().catch(() => undefined);
          setMedia(url);
          return;
        }
        const bytes = await res.arrayBuffer();
        if (abort.signal.aborted) return;
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: res.headers.get("content-type") || "audio/mpeg" }));
        setMedia(objectUrl);
        setPeaks(await readPeaks(bytes.slice(0)));
      } catch (err: any) {
        if (err?.name !== "AbortError") onError();
      }
    })();
    return () => {
      abort.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (ref.current && volume !== undefined) ref.current.volume = Math.min(1, Math.max(0, volume));
  }, [volume, media]);

  useEffect(() => {
    if (autoPlay && media) void ref.current?.play().catch(() => undefined);
  }, [autoPlay, media]);

  const toggle = () => {
    const a = ref.current;
    if (!a || !media) return;
    if (a.paused) void a.play().catch(onError);
    else a.pause();
  };
  const cycle = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length] ?? 1;
    setSpeed(next);
    if (ref.current) ref.current.playbackRate = next;
  };
  const seekTo = (fraction: number) => {
    const a = ref.current;
    if (!a || !length) return;
    const t = Math.min(length, Math.max(0, fraction * length));
    a.currentTime = t;
    setTime(t);
  };

  const shown = peaks ?? fallback;
  const progress = length ? Math.min(1, time / length) : 0;

  return (
    <div className="flex items-center gap-3">
      <audio
        ref={ref}
        src={media ?? undefined}
        preload="metadata"
        onLoadedMetadata={(e) => setLength(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); onEnded?.(); }}
        onError={onError}
      />
      <button type="button" onClick={toggle} disabled={!media} aria-label={playing ? "Pause" : "Play"} className="flex size-10 shrink-0 items-center justify-center rounded-full bg-sidebar text-white shadow-sm transition-transform hover:bg-sidebar/90 active:scale-95 disabled:opacity-60">
        {playing ? <Pause className="size-4" /> : <Play className="size-4 translate-x-px" />}
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          role="slider"
          tabIndex={0}
          aria-label="Position"
          aria-valuemin={0}
          aria-valuemax={Math.round(length)}
          aria-valuenow={Math.round(time)}
          aria-valuetext={`${clock(time)} of ${length ? clock(length) : "unknown"}`}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            seekTo((e.clientX - r.left) / r.width);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") { e.preventDefault(); seekTo(progress + 0.05); }
            else if (e.key === "ArrowLeft") { e.preventDefault(); seekTo(progress - 0.05); }
            else if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(); }
          }}
          className={cn("flex h-11 cursor-pointer items-center gap-[2px] rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring", !media && "animate-pulse")}
        >
          {shown.map((p, i) => {
            const played = (i + 0.5) / shown.length <= progress;
            return (
              <span
                key={i}
                aria-hidden="true"
                style={{ height: `${Math.round(p * 100)}%` }}
                className={cn("min-w-[2px] flex-1 rounded-full transition-colors duration-150", played ? "bg-primary" : "bg-muted-foreground/25", playing && played && i === Math.floor(progress * shown.length) && "bg-primary/70")}
              />
            );
          })}
        </div>
        <div className="flex justify-between font-mono text-[11.5px] text-muted-foreground">
          <span>{clock(time)}</span>
          <span>{length ? clock(length) : "--:--"}</span>
        </div>
      </div>
      {showSpeed && (
        <button type="button" onClick={cycle} aria-label="Playback speed" className="h-7 shrink-0 rounded-md border border-border px-2 font-mono text-xs hover:bg-muted">
          {speed}×
        </button>
      )}
    </div>
  );
}
